/**
 * Web Audio API로 효과음을 합성 — 외부 음원 파일 의존성 없음.
 * 첫 사용자 입력 후에야 AudioContext 생성 (브라우저 정책).
 */

const MUTE_KEY = 'name-nation.muted';

let ctx: AudioContext | null = null;
let muted: boolean = readMutedInitial();

function readMutedInitial(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

export function isMuted(): boolean {
  return muted;
}

export function setMuted(next: boolean): void {
  muted = next;
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(MUTE_KEY, next ? '1' : '0');
  } catch {
    // ignore
  }
}

export function toggleMuted(): boolean {
  setMuted(!muted);
  return muted;
}

function getCtx(): AudioContext | null {
  if (ctx !== null) return ctx;
  const AC =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  return ctx;
}

function beep(
  freqStart: number,
  freqEnd: number,
  durationMs: number,
  type: OscillatorType = 'sine',
  gain = 0.18,
): void {
  if (muted) return;
  const ac = getCtx();
  if (!ac) return;
  const now = ac.currentTime;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freqStart, now);
  osc.frequency.exponentialRampToValueAtTime(
    Math.max(50, freqEnd),
    now + durationMs / 1000,
  );
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(gain, now + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, now + durationMs / 1000);
  osc.connect(g).connect(ac.destination);
  osc.start(now);
  osc.stop(now + durationMs / 1000 + 0.02);
}

export const sounds = {
  correct(): void {
    beep(880, 1320, 180, 'triangle');
    setTimeout(() => beep(1320, 1760, 140, 'triangle'), 80);
  },
  wrong(): void {
    beep(330, 110, 360, 'sawtooth', 0.12);
  },
  finish(): void {
    beep(523, 523, 120, 'triangle');
    setTimeout(() => beep(659, 659, 120, 'triangle'), 130);
    setTimeout(() => beep(784, 784, 220, 'triangle'), 260);
  },
  combo(combo: number): void {
    // 콤보가 높을수록 시작 음 높아짐
    const base = 880 + Math.min(combo, 8) * 90;
    beep(base, base * 1.5, 150, 'triangle', 0.16);
    setTimeout(() => beep(base * 1.5, base * 2, 120, 'triangle', 0.14), 70);
  },
  /** 짧은 "틱" — 미스클릭/카운트다운 등 가벼운 신호용. */
  tick(freq = 440): void {
    beep(freq, freq, 60, 'sine', 0.12);
  },
  /** 시간 카운트다운 — 3·2·1초에 점진적으로 높아지는 짧은 비프. */
  countdown(secondsLeft: number): void {
    if (secondsLeft === 3) beep(660, 660, 70, 'sine', 0.13);
    else if (secondsLeft === 2) beep(880, 880, 70, 'sine', 0.13);
    else if (secondsLeft === 1) beep(1320, 1320, 110, 'sine', 0.16);
  },
};
